import { useEffect, useMemo, useState } from "react";
import AgGridWrapper from "../../UI/AgGrid";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import TableShimmer from '../../UI/Shimmer/TableShimmer';
import { toast } from "react-toastify";
import { getAllAEMAdobeIOFolders } from "../../../utils/APIs";
import { useUserContext } from "../../../contexts/UserContext";
import { useContentfulContext } from "../../../contexts/ContentfulContext";

const ViewFoldersModal = ({ onClose, integration }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { userData, updateDefaultAEMFolder } = useUserContext();
  const { content } = useContentfulContext();

  const isDefaultIntegration = integration?.default === true;

  const CheckboxRenderer = (params) => {
    const isChecked = selectedItem?.name === params.data.name && selectedItem?.href === params.data.href;

    const handleChange = () => {
      setSelectedItem(params.data);
      updateDefaultAEMFolder(params.data)
    };

    return (
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleChange}
        className="cursor-pointer rounded-lg w-4 h-4"
      />
    );
  };


  const columnDefs = useMemo(() => {
    const baseCols = [
      {
        headerName: content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.folder.table.name,
        field: "name",
        filter: true,
        sortable: true,
        suppressMovable: true,
        resizable: true,
      },
      {
        headerName: content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.folder.table.href,
        field: "href",
        filter: true,
        sortable: true,
        suppressMovable: true,
        resizable: true,
      },
    ];

    if (isDefaultIntegration) {
      baseCols.unshift({
        headerName: "",
        field: "checkbox",
        cellRenderer: CheckboxRenderer,
        suppressMovable: true,
        width: 50,
      });
    }

    return baseCols;
  }, [selectedItem, isDefaultIntegration]);

  useEffect(() => {
    if (!integration?._id) return;
    const fetchFolders = async () => {
      try {
        setLoading(true);
        const result = await getAllAEMAdobeIOFolders(
          userData.token,
          userData.selectedOrganization.org_token,
          integration._id
        );
        if (result?.status) {
          setFolders(result.data);
          const defaultFolder = result.data.find(
            (f) =>
              f.name === userData.defaultAEMFolder?.name &&
              f.href === userData.defaultAEMFolder?.href
          );
          if (defaultFolder) setSelectedItem(defaultFolder);
        }
      } catch (err) {
        toast.error("Failed to fetch folders");
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, []);


  const autoSizeStrategy = useMemo(() => ({
    type: "fitGridWidth",
    defaultMinWidth: 40,
    columnLimits: [
      { colId: "name", minWidth: 250 },
      { colId: "href", minWidth: 350 },
      { colId: "checkbox", minWidth: 40 }
    ],
  }), []);

  return (
    <PrimaryModal title={content.GLOBAL_SELECTOR_CONTENT.aem_adobe_io.folder.title} hideModal={onClose} widthClass="max-w-4xl">
      {loading ? (
        <TableShimmer colSize={2} rowSize={3} />
      ) : (
        <div className="p-7 pt-1 space-y-6">
          <div className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
            <AgGridWrapper
              tableId="global-selector-aem-adobe-io-view-folders-table"
              rowData={folders}
              columnDefs={columnDefs}
              autoSizeStrategy={autoSizeStrategy}
            />
          </div>
        </div>
      )}
    </PrimaryModal>
  );
};

export default ViewFoldersModal;
